import type { ChatOpenOptions, ChatPlugin } from '#types/plugin/chat'

declare global {
  interface Window {
    vfClarity: {
      consumer: {
        consumerId?: string | null
        isLoyaltyMember?: boolean
        locale: string
        brand: string
      }
    }
    CLARITY_HELPER: {
      invokeSearchHubOrch: (query: string) => void
      isSearch: (query: string, config?: { locale: string }) => Promise<boolean>
    }
  }
}

/**
 * Clarity AI chat provider (Bloomreach orchestrated agent).
 *
 * Unlike Sierra and Salesforce MIAW, Clarity is loaded by the Bloomreach p13n
 * module (see `domains/commerce/modules/p13n/index.ts`), so this provider does
 * not load any script or CSS. It only normalises the `open` call behind the
 * shared `$chat` plugin contract.
 *
 * - `open(query)` invokes `window.CLARITY_HELPER.invokeSearchHubOrch(query)` (defaults to `''`).
 */
export default (): ChatPlugin => {
  const { $gtm } = useNuxtApp()
  const auth = useAuthStore()
  const user = useUserStore()
  const { details, isLoyaltyMember } = storeToRefs(user)
  const { brand } = useRuntimeConfig().public
  const locale = useLocale()
  const PDP_URL_REGEX = /^\/p\/[\da-z/\-]*-[A-Z\d]{8,11}/
  const CLARITY_POLL_INTERVAL_MS = 500
  const CLARITY_MAX_ATTEMPTS = 20
  let chatInitiated = false

  watchImmediate([details, isLoyaltyMember], () => {
    window.vfClarity = {
      consumer: {
        consumerId: auth.loggedIn ? details.value?.consumerId : null,
        isLoyaltyMember: isLoyaltyMember.value,
        brand,
        locale
      }
    }
  })

  const open = (input: string | ChatOpenOptions = '') => {
    const query = typeof input === 'string' ? input : input.query ?? ''
    if (typeof input === 'object' && !chatInitiated) {
      chatInitiated = true
      $gtm.push('clarity.onInitialize', input.eventLabel, input.buttonLocation)
    }
    const tryOpen = (attempt = 0) => {
      try {
        window.CLARITY_HELPER?.invokeSearchHubOrch(query)
      }
      catch (err) {
        log.warn(`Failed to open Clarity AI chat: ${err}`)

        if (attempt < 3)
          setTimeout(tryOpen, 1000, attempt + 1)
      }
    }

    tryOpen()
  }

  const getInitializationSource = (source?: string): string => {
    if (!source) return ''

    switch (true) {
      case source.includes('PLP'):
        return 'PLP'
      case source.includes('PDP'):
        return 'PDP'
      case source.includes('Search Orchestrator') || source.includes('Search Trigger'):
        return 'Search Modal'
      default:
        return ''
    }
  }

  const handleClarityEvent = ({ detail, type }: CustomEvent) => {
    const initializationSource = getInitializationSource(detail.chat_config?.source)
    switch (type) {
      case 'ClarityTrigger:clicked':
        queueMicrotask(() => {
          if (!chatInitiated) {
            chatInitiated = true
            $gtm.push('clarity.onInitialize', detail.button_text, initializationSource)
          }
        })
        break
      case 'Clarity:userRespond':
        if (['quick_reply', 'suggestion_button'].includes(detail.type))
          $gtm.push('clarity.onAction', 'Question Click', detail.button_text, initializationSource)
        break
      case 'Clarity:chatClosed':
        chatInitiated = false
        $gtm.push('clarity.onAction', 'Close', undefined, initializationSource)
        break
      case 'Clarity:urlClick':
        if (PDP_URL_REGEX.test(detail.url)) {
          setInteractionOrigin('ask-me-anything', true)
          $gtm.push('clarity.onAction', 'Go To PDP', detail.product_id, initializationSource)
        }
        break
      case 'Clarity:clarityRespond':
        break
      case 'Clarity:miniPdpClick':
        break
    }
  }

  useEventListener(window, [
    'ClarityTrigger:clicked',
    'Clarity:userRespond',
    'Clarity:chatClosed',
    'Clarity:urlClick',
    'Clarity:clarityRespond',
    'Clarity:miniPdpClick'
  ], handleClarityEvent)

  const available = ref(!!window.CLARITY_HELPER)

  if (!available.value) {
    let attempt = 0
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const checkClarityAvailability = () => {
      if (window.CLARITY_HELPER) {
        available.value = true
        return
      }

      attempt += 1

      if (attempt >= CLARITY_MAX_ATTEMPTS) {
        available.value = true
        log.warn('Clarity AI did not become available within 10 seconds.')
        return
      }

      timeoutId = setTimeout(checkClarityAvailability, CLARITY_POLL_INTERVAL_MS)
    }

    checkClarityAvailability()

    onScopeDispose(() => {
      if (timeoutId) clearTimeout(timeoutId)
    })
  }

  return {
    open,
    available
  }
}
